import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    AppBar,
    Toolbar,
    Menu,
    MenuItem as MenuItemComponent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIcon,
    Menu as MenuIcon,
    DragIndicator as DragIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getMenus,
    createMenu,
    updateMenu,
    deleteMenu,
    updateMenuSortOrder,
} from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import Logo from "../../../assests/logo.png";

// Sortable Table Row Component
const SortableMenuRow = ({ menu, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: menu.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 50, px: 1 }}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                >
                    <DragIcon />
                </Box>
            </TableCell>
            <TableCell>{menu.menu_title}</TableCell>
            <TableCell>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "monospace",
                        color: "primary.main",
                        wordBreak: "break-all",
                    }}
                >
                    {menu.menu_link}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <IconButton
                    onClick={() => onEdit(menu)}
                    color="primary"
                    size="small"
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    onClick={() => onDelete(menu.id)}
                    color="error"
                    size="small"
                >
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const MenuManagement = () => {
    const navigate = useNavigate();
    const branchId = getCurrentBranchId();
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMenu, setEditingMenu] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [formData, setFormData] = useState({
        menu_title: "",
        menu_link: "",
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchMenus();
    }, []);

    // Fix scrolling issue
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        return () => {
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow;
            bodyElement.style.height = originalBodyHeight;
            htmlElement.style.overflow = originalHtmlOverflow;
            htmlElement.style.height = originalHtmlHeight;
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow;
                rootElement.style.height = originalRootHeight;
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    const fetchMenus = async () => {
        try {
            setLoading(true);
            const response = await getMenus(branchId);
            if (response.success) {
                setMenus(response.data);
            } else {
                setError("Failed to fetch menu items");
            }
        } catch (err) {
            setError("Error loading menu items");
            console.error("Error fetching menus:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMenu = () => {
        setEditingMenu(null);
        setFormData({ menu_title: "", menu_link: "" });
        setOpenDialog(true);
    };

    const handleEditMenu = (menu) => {
        setEditingMenu(menu);
        setFormData({
            menu_title: menu.menu_title,
            menu_link: menu.menu_link,
        });
        setOpenDialog(true);
    };

    const handleDeleteMenu = async (id) => {
        if (window.confirm("Are you sure you want to delete this menu item?")) {
            try {
                const response = await deleteMenu(id);
                if (response.success) {
                    setMenus(menus.filter((menu) => menu.id !== id));
                    setSuccess("Menu item deleted successfully");
                    setTimeout(() => setSuccess(null), 3000);
                } else {
                    setError("Failed to delete menu item");
                }
            } catch (err) {
                setError("Error deleting menu item");
                console.error("Error deleting menu:", err);
            }
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditingMenu(null);
        setFormData({ menu_title: "", menu_link: "" });
    };

    const handleSave = async () => {
        try {
            setError(null);
            const data = {
                ...formData,
                branch_id: branchId,
            };

            console.log('Saving menu:', data);

            let response;
            if (editingMenu) {
                response = await updateMenu(editingMenu.id, formData);
            } else {
                response = await createMenu(data);
            }

            console.log('Menu save response:', response);

            if (response.success) {
                await fetchMenus();
                setSuccess(
                    editingMenu
                        ? "Menu item updated successfully"
                        : "Menu item created successfully"
                );
                setTimeout(() => setSuccess(null), 3000);
                handleDialogClose();
            } else {
                const errorMsg = response.error || response.errors || "Failed to save menu item";
                setError(errorMsg);
            }
        } catch (err) {
            console.error("Error saving menu:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Error saving menu item";
            setError(errorMsg);
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigateHome = () => {
        navigate("/home");
        handleMenuClose();
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = menus.findIndex((menu) => menu.id === active.id);
        const newIndex = menus.findIndex((menu) => menu.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newItems = arrayMove(menus, oldIndex, newIndex);
        setMenus(newItems);

        // Update sort order in backend
        try {
            const menusWithNewOrder = newItems.map((menu, index) => ({
                id: parseInt(menu.id, 10),
                sort_order: parseInt(index, 10),
            }));

            console.log('Updating menu sort order:', menusWithNewOrder);
            const response = await updateMenuSortOrder(menusWithNewOrder);
            
            if (response.success) {
                setSuccess("Menu order updated successfully");
                setTimeout(() => setSuccess(null), 3000);
            } else {
                const errorMsg = response.error || response.errors || "Failed to update menu order";
                setError(errorMsg);
                // Revert the change
                setMenus(menus);
            }
        } catch (err) {
            console.error("Error updating menu sort order:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Failed to update menu order";
            setError(errorMsg);
            // Revert the change
            setMenus(menus);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
        }}>
            {/* Navbar */}
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                height: "32px",
                                marginRight: "16px",
                                filter: "brightness(0) invert(1)",
                            }}
                        />
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="back"
                            onClick={handleNavigateHome}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ flexGrow: 1 }}
                    >
                        Menu Management
                    </Typography>

                    <Button
                        color="inherit"
                        startIcon={<HomeIcon />}
                        onClick={handleNavigateHome}
                        sx={{ mr: 1 }}
                    >
                        Home
                    </Button>

                    <IconButton
                        size="large"
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenuOpen}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Navigation Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <MenuItemComponent onClick={handleNavigateHome}>
                    <HomeIcon sx={{ mr: 1 }} />
                    Admin Home
                </MenuItemComponent>
            </Menu>

            <Box sx={{ 
                p: 3,
                flex: 1,
                overflow: 'auto'
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4" component="h1">
                        Menu Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateMenu}
                    >
                        Add New Menu Item
                    </Button>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 3 }}
                        onClose={() => setSuccess(null)}
                    >
                        {success}
                    </Alert>
                )}

                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        Manage your menu items that appear in the customer-facing interface. 
                        You can create, edit, delete, and reorder menu items by dragging them. 
                        Each menu item consists of a title and a link (URL or path) that customers can access.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Tip:</strong> Drag the menu items using the drag handle (⋮⋮) to reorder them. 
                        The order you set here will be reflected in the customer interface.
                    </Typography>
                </Box>

                <TableContainer component={Paper}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 50 }}></TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Menu Title</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Menu Link</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <SortableContext
                                    items={menus.map((menu) => menu.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {menus.map((menu) => (
                                        <SortableMenuRow
                                            key={menu.id}
                                            menu={menu}
                                            onEdit={handleEditMenu}
                                            onDelete={handleDeleteMenu}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                </TableContainer>

                {menus.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No menu items found. Create your first menu item to get started!
                        </Typography>
                    </Box>
                )}

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingMenu ? "Edit Menu Item" : "Create New Menu Item"}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Menu Title"
                                value={formData.menu_title}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        menu_title: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Menu Link"
                                value={formData.menu_link}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        menu_link: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                                helperText="URL or path for the menu item (e.g., /home/about, https://example.com)"
                                inputProps={{
                                    maxLength: 500,
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.menu_title || !formData.menu_link}
                        >
                            {editingMenu ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default MenuManagement;

